import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDevicePlaneDeviceHealth } from '../api/devicePlane.js';

function rssiClass(v: number | null): string {
  if (v === null) return 'text-muted';
  return v > -65 ? 'text-goodink' : v > -75 ? 'text-warnink' : 'text-badink';
}

function uptimeLabel(sec: number | null): string {
  if (sec === null) return '—';
  if (sec < 3600) return `${Math.round(sec / 60)} m`;
  if (sec < 86400) return `${Math.round(sec / 3600)} h`;
  return `${Math.floor(sec / 86400)} d ${Math.round((sec % 86400) / 3600)} h`;
}

export function DevicePlaneHealth() {
  const { data } = useQuery({
    queryKey: ['dp-device-health'],
    queryFn: getDevicePlaneDeviceHealth,
    refetchInterval: 15000,
  });

  const items = data?.items ?? [];
  const anyReported = items.some(
    (h) => h.rssi !== null || h.heapFree !== null || h.uptimeSec !== null || h.fwVersion !== null || h.temp !== null,
  );

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-ink">Device Plane — Device health</h1>
        <p className="text-sm text-muted mt-0.5">
          Hardware health keys extracted from each device&apos;s latest telemetry reading.
        </p>
      </div>

      {!anyReported && (
        <div className="border border-dashed border-bad rounded-xl p-4 text-sm text-ink2 bg-surface">
          <b className="text-ink">No device reports health data yet.</b> The columns below fill in automatically once
          firmware adds <span className="font-mono text-xs">rssi, heapFree, uptimeSec, fwVersion, ip, temp</span> keys
          to its <span className="font-mono text-xs">telemetry.readings</span> — the middleware persists any new key
          with zero schema change, and this page reads each device&apos;s latest reading.
        </div>
      )}

      <div className="bg-surface border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-muted border-b border-grid">
              <th className="px-4 py-2.5">Serial</th>
              <th className="px-4 py-2.5">Category</th>
              <th className="px-4 py-2.5">Presence</th>
              <th className="px-4 py-2.5 text-right">WiFi RSSI</th>
              <th className="px-4 py-2.5 text-right">Free heap</th>
              <th className="px-4 py-2.5 text-right">Uptime</th>
              <th className="px-4 py-2.5">Firmware</th>
              <th className="px-4 py-2.5">IP</th>
              <th className="px-4 py-2.5 text-right">Temp</th>
              <th className="px-4 py-2.5">Last reading</th>
            </tr>
          </thead>
          <tbody>
            {items.map((h) => (
              <tr key={h.deviceId} className="border-b border-grid hover:bg-surface2">
                <td className="px-4 py-2.5 font-mono text-xs">
                  <Link to={`/device-plane/fleet/${h.deviceId}`} className="text-accent hover:underline">
                    {h.serialNumber}
                  </Link>
                </td>
                <td className="px-4 py-2.5 text-xs text-ink2">{h.kind}</td>
                <td className="px-4 py-2.5">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                    h.presence === 'online' ? 'text-goodink' : h.presence === 'offline' ? 'text-badink' : 'text-muted'
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${
                      h.presence === 'online' ? 'bg-good' : h.presence === 'offline' ? 'bg-bad' : 'bg-muted'
                    }`} />
                    {h.presence}
                  </span>
                </td>
                <td className={`px-4 py-2.5 text-right tabular-nums text-xs ${rssiClass(h.rssi)}`}>
                  {h.rssi === null ? '—' : `${h.rssi} dBm`}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-xs text-ink2">
                  {h.heapFree === null ? '—' : `${h.heapFree} kB`}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-xs text-ink2">{uptimeLabel(h.uptimeSec)}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-ink2">{h.fwVersion ?? '—'}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-muted">{h.ip ?? '—'}</td>
                <td className={`px-4 py-2.5 text-right tabular-nums text-xs ${
                  h.temp !== null && h.temp >= 45 ? 'text-warnink' : 'text-ink2'
                }`}>
                  {h.temp === null ? '—' : `${h.temp} °C`}
                </td>
                <td className="px-4 py-2.5 text-xs text-muted">
                  {h.ts ? new Date(h.ts).toLocaleString() : 'no telemetry'}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-sm text-muted">
                  No devices in the registry yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
